import React, { Component, Fragment } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import Select from 'react-select';
import { find, propEq, compose, map, defaultTo, path, reduce, isEmpty } from 'ramda';

import magic from '../../util/magic';
import { handleErrorBase, handleSuccessBase } from '../../util/forms';
import { addPrices, toCurrency } from '../../util/helpers';
import { isNilOrEmpty } from '../../util/redux';
import registrationActions from '../../redux/registration';
import {
  apiApproveRegistration,
  apiApproveModule,
  apiViewCanvasSectionsForRegistrationCourseMaterials,
} from '../../api/registration';
import { apiGetEmailDropdownsForCategory } from '../../api/email';

import Modal from '../Modal';
import EditField from '../Forms/EditField';

const mapDispatch = {
  ...registrationActions,
};

const mapStore = ({ certifications }) => ({ certifications });

const propTypes = {
  certifications: PropTypes.array,
  registration: PropTypes.object,
  trainingId: PropTypes.number,
  updateRegistration: PropTypes.func,
};

class ApproveRegistrationModal extends Component {
  state = {
    courseMaterials: [],
    courseMaterialSections: [],
    message: '',
    fetchDropdowns: false,
    messageDropdown: [],
    selectedMessages: [],
    selectedSections: [],
  };

  loadingModuleSelections = false;

  static getDerivedStateFromProps(nextProps, prevState) {
    if (prevState && !prevState.show && nextProps.show) {
      return { show: defaultTo(false, nextProps.show), fetchDropdowns: true };
    }

    return { show: defaultTo(false, nextProps.show) };
  }

  componentDidUpdate(prevProps, prevState) {
    if (this.state.fetchDropdowns && !this.loadingModuleSelections) {
      this.loadingModuleSelections = true;

      apiGetEmailDropdownsForCategory(magic.emailRegistrationApproval)
        .then(({ data }) => this.setState({ messageDropdown: data.options, fetchDropdowns: false }))
        .catch((err) => console.error(err))
        .then(() => apiViewCanvasSectionsForRegistrationCourseMaterials(this.props.registration.id))
        .then(({ data }) => this.setState({ courseMaterialSections: data }))
        .catch((err) => console.error(err))
        .then(() => (this.loadingModuleSelections = false));
    }
  }

  renderApprovalMessageBoxes = () => {
    const { selectedMessages, messageDropdown, message } = this.state;

    return (
      <Fragment>
        <EditField
          as={Select}
          bare
          block
          multi
          label="Approval message (optional)"
          name="messageDropdown"
          onChange={(value) => this.setState({ selectedMessages: value.map((choice) => choice.value) })}
          value={selectedMessages}
          options={messageDropdown.map((option) => ({ value: option.id, label: option.title }))}
        />
        <EditField
          block
          as="textarea"
          name="message"
          onChange={({ target }) => this.setState({ message: target.value })}
          value={message}
        />
      </Fragment>
    );
  };

  renderCanvasSectionSelection = () => {
    const { registration } = this.props;
    const { courseMaterialSections, selectedSections } = this.state;
    if (isEmpty(courseMaterialSections)) return;

    const materialsWithCanvasCourses = registration.courseMaterials.filter(
      (material) =>
        !isNilOrEmpty(material.canvasCourseId) ||
        !isNilOrEmpty(material.canvasCourse2Id) ||
        !isNilOrEmpty(material.canvasCourse3Id),
    );
    if (isEmpty(materialsWithCanvasCourses)) return;

    const renderSelectForMaterialAndCourse = (material, courseId) =>
      courseId ? (
        <EditField
          key={courseId}
          as={Select}
          bare
          block
          simpleValue
          label={`${material.name} (Canvas ID #${courseId}) Section`}
          name={`selectedSections[${courseId}]`}
          onChange={(value) => this.setState({ selectedSections: { ...selectedSections, [courseId]: value } })}
          value={selectedSections[courseId]}
          options={courseMaterialSections[courseId].map((option) => ({
            value: option.id,
            label: option.name,
          }))}
        />
      ) : (
        <noscript />
      );

    return materialsWithCanvasCourses.map((material) => (
      <Fragment>
        {renderSelectForMaterialAndCourse(material, material.canvasCourseId)}
        {renderSelectForMaterialAndCourse(material, material.canvasCourse2Id)}
        {renderSelectForMaterialAndCourse(material, material.canvasCourse3Id)}
      </Fragment>
    ));
  };

  renderRegistrationApproval = () => {
    const { registration } = this.props;
    const trainings = defaultTo([], registration.trainings);
    const fees = defaultTo([], registration.registrationFees);
    const internationalFees = defaultTo([], registration.internationalFees).filter((fee) => fee.materials == true);
    const materials = defaultTo([], registration.courseMaterials);
    const events = defaultTo([], registration.events);
    const membership = defaultTo({}, path(['memberships', 0], registration));
    const donation = defaultTo({}, path(['donations', 0], registration));
    donation.price = donation.finalPrice;

    const totalPrices = reduce(addPrices, { total: 0, afterDiscount: 0 }, [
      ...trainings,
      ...fees,
      ...internationalFees,
      ...materials,
      ...events,
      membership,
      donation,
    ]);

    return (
      <Fragment>
        <p>
          Are you sure you want to approve the registration and charge the remaining{' '}
          {toCurrency(totalPrices.afterDiscount)} to the user's credit card?
        </p>
        <div>This includes any unpaid:</div>
        <ul>
          <li>Fees</li>
          <li>Course Materials</li>
          <li>Events</li>
          <li>Memberships</li>
          <li>Donations</li>
        </ul>
        {this.renderCanvasSectionSelection()}
        {registration.certificationId && this.renderApprovalMessageBoxes()}
      </Fragment>
    );
  };

  renderModuleApproval = () => {
    return <p>Are you sure you want to approve the training and issue the badge?</p>;
  };

  renderExperienceApproval = () => {
    return <p>Are you sure you want to approve the registration's experience hours?</p>;
  };

  renderFinalApproval = () => {
    const { message } = this.state;

    return (
      <Fragment>
        <p>Are you sure you want to approve the registration and issue the certification?</p>
        <EditField
          block
          as="textarea"
          name="message"
          label="Message to the certificant (Optional):"
          onChange={({ target }) => this.setState({ message: target.value })}
          value={message}
        />
      </Fragment>
    );
  };

  renderPathwayApproval = () => {
    const { certifications, registration } = this.props;
    const { courseMaterials, message } = this.state;
    const certification = find(propEq('id', registration.certificationId))(certifications);
    const pathway = find(propEq('id', registration.pathwayTypeId), certification.pathwayTypes);
    const materials = compose(
      map((material) => ({ value: material.id, label: material.name })),
      defaultTo([]),
    )(pathway.courseMaterials);

    return (
      <Fragment>
        <label>Choose Course Materials:</label>
        <Select
          multi
          className="mb-3"
          onChange={(value) => this.setState({ courseMaterials: value.map((choice) => choice.value) })}
          options={materials}
          value={courseMaterials}
        />
        <EditField
          block
          as="textarea"
          name="message"
          label="Message to the registrant (Optional):"
          onChange={({ target }) => this.setState({ message: target.value })}
          value={message}
        />
      </Fragment>
    );
  };

  render() {
    const { onHide, registration, show, updateRegistration, trainingId } = this.props;
    const isModuleApproval = registration.status == magic.moduleApproval;
    const isFinalApproval = registration.status == magic.finalApproval;
    const isExperienceApproval = registration.status == magic.experienceApproval;
    const isPathwayApproval = registration.step == magic.pathwayApprovalStep;
    const approvalFunction = isModuleApproval ? apiApproveModule : apiApproveRegistration;

    return (
      <Modal show={show} onHide={onHide} title="Approve Registration">
        <Modal.Body>
          {isModuleApproval
            ? this.renderModuleApproval()
            : isFinalApproval
            ? this.renderFinalApproval()
            : isExperienceApproval
            ? this.renderExperienceApproval()
            : isPathwayApproval
            ? this.renderPathwayApproval()
            : this.renderRegistrationApproval()}
        </Modal.Body>
        <Modal.Footer>
          <button type="button" className="btn btn-secondary" onClick={onHide}>
            Close
          </button>
          <button
            type="submit"
            className="btn btn-primary"
            onClick={() =>
              approvalFunction(registration.id, { trainingId, ...this.state })
                .then((resp) => updateRegistration(resp.data))
                .then(() => handleSuccessBase('Success'))
                .then(onHide)
                .catch(() => handleErrorBase('There was a problem approving the registration'))
            }
          >
            Approve
          </button>
        </Modal.Footer>
      </Modal>
    );
  }
}

ApproveRegistrationModal.propTypes = propTypes;

export default connect(mapStore, mapDispatch)(ApproveRegistrationModal);
